import { useApolloClient, useSuspenseQuery } from '@apollo/client';
import {
  CalendarClockIcon,
  ChevronRight,
  ContactRoundIcon,
  User,
  UserCogIcon,
} from 'lucide-react';
import { Suspense } from 'react';
import { Link } from 'react-router-dom';

import {
  Badge,
  Box,
  Card,
  H5,
  HStack,
  P,
  Skeleton,
  cn,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';

import { GetTenantsList } from '../operations';

const listItemClassName = cn(
  'w-full min-w-[320px] grid items-center gap-2',
  'md:grid-cols-[40px_1fr_2fr_40px] lg:grid-cols-[40px_1fr_1fr_40px]',
  'grid-cols-[40px_1fr_40px]',
);

const listItemInfoClassName = cn(
  'grid grid-cols-3 gap-2 items-center justify-items-center text-text-supporting',
  '*:text-center  *:whitespace-nowrap',
  'md:row-start-auto md:col-span-1 md:border-t-0 md:pt-0 md:text-text-tertiary',
  'row-start-2 col-span-3 border-t border-border-secondary pt-2',
);

interface TenantListItemProps {
  name: React.ReactNode;
  role: React.ReactNode;
  memberSince: React.ReactNode;
  tenantCreatedAt: React.ReactNode;
  isPendingApproval: boolean;
}

const TenantListItem: React.FC<TenantListItemProps> = ({
  name,
  role,
  memberSince,
  tenantCreatedAt,
  isPendingApproval,
}) => {
  return (
    <Card className={cn('px-2 py-3 md:py-4', listItemClassName)}>
      <Box center className="mr-1 border-r border-r-bg-quaternary pr-1">
        <User className="text-bg-accent-strong-secondary/80" />
      </Box>
      <HStack>
        <H5>{name}</H5>
        {isPendingApproval && (
          <Badge className="pointer-events-none bg-bg-warning-strong-primary text-text-warning-strong">
            Pending Approval
          </Badge>
        )}
      </HStack>
      <div className={listItemInfoClassName}>
        <P>{role}</P>
        <Box hStack className="gap-2 justify-self-start md:justify-self-center">
          <ContactRoundIcon className="size-5 text-text-supporting md:hidden" />
          <P>{memberSince}</P>
        </Box>
        <Box hStack className="gap-2 justify-self-start md:justify-self-center">
          <CalendarClockIcon className="size-5 text-text-supporting md:hidden" />
          <P>{tenantCreatedAt}</P>
        </Box>
      </div>
      <ChevronRight className="h-6 w-10 border-l-2 border-l-bg-quaternary pl-2 text-bg-accent-strong-secondary" />
    </Card>
  );
};

const TenantsListContent: React.FC = () => {
  const { data } = useSuspenseQuery(GetTenantsList);
  const client = useApolloClient();

  return (
    <>
      {data.assignedTenantUsers.map((tenant) => {
        if (!tenant.tenantId) {
          return null;
        }

        return (
          <Link
            to={tenant.tenantId}
            key={tenant.tenantId}
            className={cn(
              'group drop-shadow-none hover:drop-shadow-lg',
              'transition-all duration-200 ease-in-out',
            )}
            onClick={() => {
              // Clear the apollo cache if we're switching tenants
              client.resetStore();
            }}
          >
            <TenantListItem
              name={tenant.name}
              role={tenant.role}
              memberSince={formatToLocale(tenant.memberSince, { noTime: true })}
              tenantCreatedAt={formatToLocale(tenant.tenantCreatedAt, {
                noTime: true,
              })}
              isPendingApproval={tenant.isPendingApproval ?? false}
            />
          </Link>
        );
      })}
    </>
  );
};

export const TenantsList = () => {
  return (
    <div className="flex max-h-full w-full flex-col gap-2 overflow-auto p-2">
      <div className={cn('!hidden px-5 py-1 md:!grid', listItemClassName)}>
        <div />
        <P>Organization</P>
        <div className={listItemInfoClassName}>
          <Box hStack className="gap-4">
            <UserCogIcon className="size-5 text-text-supporting" />
            <P>Role</P>
          </Box>
          <Box hStack className="gap-4">
            <ContactRoundIcon className="size-5 text-text-supporting" />
            <P>Member Since</P>
          </Box>
          <Box hStack className="gap-4">
            <CalendarClockIcon className="size-5 text-text-supporting" />
            <P>Created at</P>
          </Box>
        </div>
      </div>
      <Suspense
        fallback={
          <>
            {[...Array(4)].map((_, index) => (
              <TenantListItem
                key={index}
                name={<Skeleton className="h-4 w-[200px] bg-border-primary" />}
                role={
                  <Box fullWidth center>
                    <Skeleton className="h-4 w-[70px] bg-border-primary" />
                  </Box>
                }
                memberSince={
                  <Box fullWidth center>
                    <Skeleton className="h-4 w-[70px] bg-border-primary" />
                  </Box>
                }
                tenantCreatedAt={
                  <Box fullWidth center>
                    <Skeleton className="h-4 w-[70px] bg-border-primary" />
                  </Box>
                }
                isPendingApproval={false}
              />
            ))}
          </>
        }
      >
        <TenantsListContent />
      </Suspense>
    </div>
  );
};
